import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotifierService } from 'angular-notifier';
import { filter } from 'rxjs/operators';
import {
  CreateRestaurantModem,
  IRestaurantModem,
  UpdateRestaurantModem,
} from '../../../interfaces/restaurant';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { ConfirmationDialogComponent } from '../../modals/confirm-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-restaurant-modems',
  templateUrl: './restaurant-modems.component.html',
  styleUrls: ['./restaurant-modems.component.scss']
})
export class RestaurantModemsComponent implements OnChanges {
  @Input() restaurantId: number | null = null;
  @Input() editable = false;

  public readonly shortFieldMaxLength = 255;
  public readonly notesMaxLength = 10000;
  public readonly phoneRegex = /^\+?\d+$/;

  public modems: IRestaurantModem[] = [];
  public modemsLoading = false;
  public modemForm: FormGroup = this.generateForm(undefined, true);
  public editingModemId: number | null = null;
  public editModemForm: FormGroup = this.generateForm();
  public savingModem = false;
  public deletingModemId: number | null = null;
  public expandedNotesModemIds = new Set<number>();

  constructor(
    private formBuilder: FormBuilder,
    private restaurantService: RestaurantService,
    private matDialog: MatDialog,
    private notifier: NotifierService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.restaurantId && this.restaurantId) {
      this.loadModems();
    }

    if (changes.editable && !this.editable) {
      this.cancelEdit();
    }
  }

  public loadModems(): void {
    if (!this.restaurantId) {
      return;
    }

    this.modemsLoading = true;
    this.restaurantService.getRestaurantModems(this.restaurantId).subscribe((modems) => {
      this.modems = modems;
      this.expandedNotesModemIds.clear();
      this.modemsLoading = false;
    }, () => {
      this.modemsLoading = false;
    });
  }

  public createModem(): void {
    if (!this.editable || !this.restaurantId || this.modemForm.invalid || this.savingModem) {
      return;
    }

    this.savingModem = true;
    this.restaurantService.createRestaurantModem(this.restaurantId, this.buildCreatePayload())
      .subscribe((modem) => {
        this.modems.unshift(modem);
        this.modemForm.reset();
        this.savingModem = false;
        this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.addedModem'));
      }, () => {
        this.savingModem = false;
      });
  }

  public startEdit(modem: IRestaurantModem): void {
    if (!this.editable) {
      return;
    }

    if (this.editingModemId === modem.id) {
      this.cancelActiveEdit(modem);
      return;
    }

    if (this.editingModemId && this.editModemForm.dirty) {
      this.confirmDiscardChanges(() => this.beginEdit(modem));
      return;
    }

    this.beginEdit(modem);
  }

  private beginEdit(modem: IRestaurantModem): void {
    this.editingModemId = modem.id;
    this.editModemForm = this.generateForm(modem);
  }

  public cancelEdit(): void {
    this.editingModemId = null;
    this.editModemForm = this.generateForm();
  }

  public isNotesExpanded(modemId: number): boolean {
    return this.expandedNotesModemIds.has(modemId);
  }

  public toggleNotes(modemId: number): void {
    if (this.expandedNotesModemIds.has(modemId)) {
      this.expandedNotesModemIds.delete(modemId);
      return;
    }

    this.expandedNotesModemIds.add(modemId);
  }

  public shouldShowNotesToggle(notes: string | null): boolean {
    return !!notes && notes.length > 180;
  }

  private cancelActiveEdit(modem: IRestaurantModem): void {
    if (!this.editModemForm.dirty) {
      this.cancelEdit();
      return;
    }

    this.confirmDiscardChanges(() => {
      this.editModemForm = this.generateForm(modem);
      this.cancelEdit();
    });
  }

  private confirmDiscardChanges(onConfirm: () => void): void {
    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('pages.restaurantAccount.unsavedModemTitle'),
        message: this.translate.instant('pages.restaurantAccount.unsavedModemConfirm'),
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      onConfirm();
    });
  }

  public updateModem(): void {
    if (!this.editable || !this.restaurantId || !this.editingModemId || this.editModemForm.invalid || this.savingModem) {
      return;
    }

    this.savingModem = true;
    this.restaurantService.updateRestaurantModem(
      this.restaurantId,
      this.editingModemId,
      this.buildUpdatePayload(this.editModemForm),
    ).subscribe((updatedModem) => {
      this.modems = this.modems.map((modem) => modem.id === updatedModem.id ? updatedModem : modem);
      this.cancelEdit();
      this.savingModem = false;
      this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.editedModem'));
    }, () => {
      this.savingModem = false;
    });
  }

  public confirmDelete(modem: IRestaurantModem): void {
    if (!this.editable || !this.restaurantId) {
      return;
    }

    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('pages.restaurantAccount.deleteModem'),
        message: this.translate.instant('pages.restaurantAccount.deleteModemConfirm'),
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.deleteModem(modem.id);
    });
  }

  private deleteModem(modemId: number): void {
    if (!this.restaurantId) {
      return;
    }

    this.deletingModemId = modemId;
    this.restaurantService.deleteRestaurantModem(this.restaurantId, modemId)
      .subscribe(() => {
        this.modems = this.modems.filter((modem) => modem.id !== modemId);
        this.expandedNotesModemIds.delete(modemId);
        if (this.editingModemId === modemId) {
          this.cancelEdit();
        }
        this.deletingModemId = null;
        this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.deletedModem'));
      }, () => {
        this.deletingModemId = null;
      });
  }

  private generateForm(modem?: IRestaurantModem, requireValue = false): FormGroup {
    const form = this.formBuilder.group({
      phoneNumber: [modem?.phoneNumber ?? '', [
        Validators.maxLength(this.shortFieldMaxLength),
        Validators.pattern(this.phoneRegex)
      ]],
      routerInfo: [modem?.routerInfo ?? '', [Validators.maxLength(this.shortFieldMaxLength)]],
      notes: [modem?.notes ?? '', [Validators.maxLength(this.notesMaxLength)]],
    });

    if (requireValue) {
      form.setValidators(this.atLeastOneModemFieldValidator);
    }

    return form;
  }

  private atLeastOneModemFieldValidator(control: AbstractControl): ValidationErrors | null {
    const hasValue = ['phoneNumber', 'routerInfo', 'notes'].some((controlName) => {
      const value = control.get(controlName)?.value;

      return typeof value === 'string' && value.trim().length > 0;
    });

    return hasValue ? null : { modemValueRequired: true };
  }

  private buildCreatePayload(): CreateRestaurantModem {
    const payload: CreateRestaurantModem = {};
    const phoneNumber = this.getTrimmedValue(this.modemForm, 'phoneNumber');
    const routerInfo = this.getTrimmedValue(this.modemForm, 'routerInfo');
    const notes = this.getTrimmedValue(this.modemForm, 'notes');

    if (phoneNumber !== null) {
      payload.phoneNumber = phoneNumber;
    }

    if (routerInfo !== null) {
      payload.routerInfo = routerInfo;
    }

    if (notes !== null) {
      payload.notes = notes;
    }

    return payload;
  }

  private buildUpdatePayload(form: FormGroup): UpdateRestaurantModem {
    const payload: UpdateRestaurantModem = {};

    if (form.get('phoneNumber')?.dirty) {
      payload.phoneNumber = this.getTrimmedValue(form, 'phoneNumber');
    }

    if (form.get('routerInfo')?.dirty) {
      payload.routerInfo = this.getTrimmedValue(form, 'routerInfo');
    }

    if (form.get('notes')?.dirty) {
      payload.notes = this.getTrimmedValue(form, 'notes');
    }

    return payload;
  }

  private getTrimmedValue(form: FormGroup, controlName: string): string | null {
    const value = form.get(controlName)?.value;

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();

    return trimmedValue ? trimmedValue : null;
  }
}
